// [CurveBeam] - [BoneVue]

import { BmdUtils, BmdViewportRotation, Types } from '@ohif/core';
import { CommandsManager } from '../../classes';
import ServicesManager from '../ServicesManager';
import { PubSubService } from '../_shared/pubSubServiceInterface';
import { BmdApiService } from './apis/bmd-api.service';
import { ScanDetail } from './models/scan.interface';
import { DicomSr } from './models/bmd.model';
import { loadSegmentation } from './utils/loadSegmentation';
import { ButtonEnums } from '@ohif/ui';
import { GenerateRoiProjectionRequest } from './apis/dtos/result.dto';
import { ResultStatus } from './models/result.interface';

/**
 * Central services that enables the data transmission between ReactJS UI and tools
 */
export class BmdService extends PubSubService {
  static REGISTRATION = {
    name: 'bmdService',
    create: ({
      commandsManager,
      servicesManager,
    }: Types.Extensions.ExtensionParams): BmdService => {
      const bmdApiService = BmdApiService.getInstance();

      return new BmdService(commandsManager, servicesManager, bmdApiService);
    },
  };

  // - For BmdRotationTool
  rotationStack: BmdViewportRotation[] = [];
  originalRotationInfo: BmdViewportRotation[] = []; // Store original rotations from server
  initialized = false;

  // - Roi data
  organizationId: string;
  studyUID: string;
  seriesUID: string;
  resultUID: string;
  scanDetail: ScanDetail;

  private currentDicomSr: DicomSr = undefined;
  private dicomSrs: DicomSr[] = [];

  getDicomSrs(): DicomSr[] {
    return [...this.dicomSrs];
  }

  // - Events for BmdRotationTool to subscribe
  resetCameraRotation?: () => void;

  // - Events for React UI to subscribe
  scanDetailUpdated?: (scanDetail: ScanDetail) => void;
  showError?: (message: string) => void;
  showLoadingToast?: (message: string, durationInMS: number) => void;
  showInfo?: (message: string) => void;
  showSuccess?: (message: string) => void;
  initializingUpdated?: (initializing: boolean) => void;
  apiLoadingUpdated?: (loading: boolean) => void;

  // - Some helper properties
  initializing = false;
  apiLoading = false;

  constructor(
    private commandsManager: CommandsManager,
    private servicesManager: ServicesManager,
    public bmdApiService: BmdApiService
  ) {
    super(null);
  }

  setInitializing(initializing: boolean): void {
    this.initializing = initializing;
    this.initializingUpdated?.(initializing);
  }

  setApiLoading(loading: boolean): void {
    this.apiLoading = loading;
    this.apiLoadingUpdated?.(loading);
  }

  /**
   * Called once MPR view initialized
   */
  async init(): Promise<void> {
    if (this.initialized || !BmdUtils.isBmd()) {
      return;
    }

    try {
      this.setInitializing(true);
      // - Get result details from the backend
      const { organizationId, studyUID, seriesUID, resultUID } = BmdUtils.getBmdParamsFromURL();
      this.organizationId = organizationId;
      this.studyUID = studyUID;
      this.seriesUID = seriesUID;
      this.resultUID = resultUID;

      await this.initScan();

      if (this.scanDetail.status !== ResultStatus.BmdProcessed) {
        this.showWarningToast(
          'BoneVue analysis is in progress, please wait for the result. Please check the progress in BoneVue platform'
        );
        return;
      }

      if (BmdUtils.isSegmentation()) {
        if (!this.scanDetail.segmentationUrl) {
          throw new Error('Segmentation URL is missing');
        }

        if (this.scanDetail.segmentationUrl) {
          this.dicomSrs = await loadSegmentation(
            this.servicesManager,
            this.scanDetail.segmentationUrl,
            this.seriesUID
          );
        }
      }

      this.initialized = true;
      this.setInitializing(false);
      console.log('BmdService initialized');
    } catch (err) {
      this.handleError(err);
    }
  }

  //#region Api calls

  /**
   * Initialize result info
   * - Get result info from the backend
   */
  private async initScan(): Promise<void> {
    try {
      this.setApiLoading(true);
      this.scanDetail = undefined;

      const response = await this.bmdApiService.getScanDetails({
        organizationId: this.organizationId,
        seriesUID: this.seriesUID,
      });
      this.scanDetailChanged(response);
      console.log('Scan initialized', { scan: response });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setApiLoading(false);
    }
  }

  async rotate(): Promise<void> {
    try {
      this.setApiLoading(true);

      // Combine pre-existing rotations from server with new manual rotations
      // Pre-existing rotations are stored in originalRotationInfo (saved at load time)
      // New manual rotations are in rotationStack (already in correct server format)
      const preExistingRotations = this.originalRotationInfo || [];
      const manualRotations = this.rotationStack.map(rotation => ({
        viewportName: rotation.viewportName,
        rotation: rotation.rotation, // No conversion needed - already in server format
      }));

      // Merge: pre-existing + manual rotations
      const rotationInfo = [...preExistingRotations, ...manualRotations];

      // Console log rotation data instead of submitting
      console.log('=== ROTATION DATA ===');
      console.log('Organization ID:', this.organizationId);
      console.log('Series UID:', this.seriesUID);
      console.log('Result UID:', this.resultUID);
      console.log('\n--- Pre-existing Rotations (from server) ---');
      console.log(JSON.stringify(preExistingRotations, null, 2));
      if (preExistingRotations.length > 0) {
        console.log('Angles in degrees:');
        preExistingRotations.forEach(r => {
          console.log(`  ${r.viewportName}: ${((r.rotation * 180) / Math.PI).toFixed(2)}°`);
        });
      }

      console.log('\n--- New Manual Rotations ---');
      console.log(JSON.stringify(this.rotationStack, null, 2));
      if (this.rotationStack.length > 0) {
        console.log('Angles in degrees:');
        this.rotationStack.forEach(r => {
          console.log(`  ${r.viewportName}: ${((r.rotation * 180) / Math.PI).toFixed(2)}°`);
        });
      }

      console.log('\n--- Combined Rotation Info to Submit ---');
      console.log(JSON.stringify(rotationInfo, null, 2));
      console.log('Total Rotations:', rotationInfo.length);
      if (rotationInfo.length > 0) {
        console.log('All angles in degrees:');
        rotationInfo.forEach(r => {
          console.log(`  ${r.viewportName}: ${((r.rotation * 180) / Math.PI).toFixed(2)}°`);
        });
      }

      // Submit rotation data to server
      const response = await this.bmdApiService.rotate({
        organizationId: this.organizationId,
        seriesUID: this.seriesUID,
        resultUID: this.resultUID,
        rotationInfo,
      });
      this.scanDetailChanged(response);
      this.showSuccessToast('Rotation saved, this window will automatically close in 5 seconds');
      setTimeout(() => {
        window.close();
      }, 5000);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setApiLoading(false);
    }
  }

  // eslint-disable-next-line
  async saveSegmentation(part10Buffer: any) {
    try {
      if (!this.scanDetail.segmentationUploadUrl) {
        this.showErrorToast('Segmentation upload URL is missing');
        return;
      }

      this.setApiLoading(true);
      const request: GenerateRoiProjectionRequest = {
        organizationId: this.organizationId,
        seriesUID: this.seriesUID,
        resultUID: this.resultUID,
      };
      const response = await this.bmdApiService.saveSegmentation(
        request,
        this.scanDetail.segmentationUploadUrl,
        part10Buffer
      );
      this.scanDetailChanged(response);
      this.showSuccessToast(
        'Segmentation saved, this window will automatically close in 5 seconds'
      );
      setTimeout(() => {
        window.close();
      }, 5000);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setApiLoading(false);
    }
  }

  //#endregion

  private scanDetailChanged(scanDetail: ScanDetail) {
    this.scanDetail = scanDetail;
    this.scanDetailUpdated?.(scanDetail);
  }

  private handleError(error: unknown) {
    if (error instanceof Error) {
      this.showErrorToast(error.message);
      console.error(error.message);
      return;
    }

    this.showErrorToast(error as string);
    console.error(error, {
      organizationId: this.organizationId,
      seriesUID: this.seriesUID,
      resultUID: this.resultUID,
    });
  }

  setCurrentDicomSr(dicomSrMappings: DicomSr): void {
    this.currentDicomSr = dicomSrMappings;
  }

  getCurrentDicomSr(): DicomSr {
    return this.currentDicomSr;
  }

  findDicomSrBySource(sourceSeriesId: string): DicomSr {
    return this.dicomSrs.find(dicomSr => dicomSr.sourceSeriesUID === sourceSeriesId);
  }

  private askSave(message: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      const { uiViewportDialogService, viewportGridService } = this.servicesManager.services;
      const viewportId = viewportGridService.getActiveViewportId();

      const actions = [
        {
          type: ButtonEnums.type.secondary,
          text: 'Cancel',
          value: false,
        },
        {
          type: ButtonEnums.type.primary,
          text: 'Yes',
          value: true,
        },
      ];
      const onSubmit = result => {
        uiViewportDialogService.hide();
        resolve(result);
      };

      uiViewportDialogService.show({
        id: 'askSave',
        viewportId,
        type: 'warning',
        message,
        actions,
        onSubmit,
        onOutsideClick: () => {},
        onKeyPress: event => {
          if (event.key === 'Enter') {
            onSubmit(true);
          }
        },
      });
    });
  }

  showToast(message: string, type: 'success' | 'error' | 'warning') {
    const { uiNotificationService } = this.servicesManager.services;
    uiNotificationService.show({
      title: type.charAt(0).toUpperCase() + type.slice(1),
      message,
      type,
    });
  }

  showSuccessToast(message: string) {
    this.showToast(message, 'success');
  }

  showErrorToast(message: string) {
    this.showToast(message, 'error');
  }

  showWarningToast(message: string) {
    this.showToast(message, 'warning');
  }
}
